import { useState, useEffect } from "react";
import { API, useAuth , authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { SyncNowButton } from "@/components/SyncNowButton";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { mutate, confirmDelete } from "@/lib/apiMutate";
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const EmployeesPage = () => {
  const { user } = useAuth();
  const canEdit = useEditMode();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [employees, setEmployees] = useState([]);  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState(user?.region_id || "all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_code: "",
    full_name: "",
    date_of_joining: "",
    mobile_number: "",
    role: "SALES_EXECUTIVE",
    district: "",
    region_id: user?.region_id || "",
    status: "ACTIVE",
    home_address: "",
    google_location: "",
    blood_group: "",
    aadhar_number: "",
    pan_number: "",
    emergency_contact: "",
    date_of_birth: "",
    bank_account_number: "",
    bank_name: "",
    ifsc_code: "",
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [loginEmployee, setLoginEmployee] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "", role: "SALES_REP", mobile_login_enabled: true });
  const [loginCreating, setLoginCreating] = useState(false);

  const openCreateLogin = (employee) => {
    setLoginEmployee(employee);
    const guessEmail = (employee.full_name || "").toLowerCase().replace(/\s+/g, ".") + "@avicement.com";
    setLoginForm({
      email: guessEmail,
      password: "",
      role: ["SALES_EXECUTIVE", "FIELD_EXECUTIVE", "SALESMAN"].includes((employee.role || "").toUpperCase()) ? "SALES_REP" : "SALES_REP",
      mobile_login_enabled: true,
    });
  };

  const handleCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginEmployee) return;
    setLoginCreating(true);
    try {
      const res = await authFetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: loginEmployee.full_name,
          email: loginForm.email.trim().toLowerCase(),
          password: loginForm.password,
          role: loginForm.role,
          region_id: loginEmployee.region_id || null,
          linked_employee_id: loginEmployee.employee_id,
          mobile_login_enabled: loginForm.mobile_login_enabled,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Login created for ${loginEmployee.full_name} — they can sign into ${loginForm.mobile_login_enabled ? "web + mobile" : "web only"}`);
        setLoginEmployee(null);
        fetchEmployees();
      } else {
        toast.error(data.detail || "Failed to create login");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setLoginCreating(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [search, roleFilter, statusFilter, regionFilter]);

  const fetchEmployees = async () => {
    try {
      let url = `${API}/employees?`;
      if (search) url += `search=${search}&`;
      if (roleFilter && roleFilter !== "all") url += `role=${roleFilter}&`;
      if (statusFilter && statusFilter !== "all") url += `status=${statusFilter}&`;
      if (regionFilter && regionFilter !== "all") url += `region=${regionFilter}&`;

      const response = await authFetch(url, {  });
      const data = await response.json();
      if (data.success) {
        setEmployees(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingEmployee
      ? `${API}/employees/${editingEmployee.employee_id}`
      : `${API}/employees`;
    const r = await mutate(
      authFetch,
      url,
      {
        method: editingEmployee ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      },
      {
        successMessage: editingEmployee ? "Employee updated" : "Employee added",
        failureMessage: "Failed to save employee",
      },
    );
    if (!r.ok) return;
    setIsDialogOpen(false);
    setEditingEmployee(null);
    resetForm();
    fetchEmployees();
  };

  const handleDelete = async (employeeId) => {
    const r = await confirmDelete(authFetch, `${API}/employees/${employeeId}`, {
      promptMessage: "Are you sure you want to deactivate this employee?",
      successMessage: "Employee deactivated",
      failureMessage: "Failed to deactivate employee",
      onSuccess: () => setEmployees((prev) => prev.filter((e) => e.employee_id !== employeeId)),
    });
    if (r.ok) fetchEmployees();
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_code: employee.employee_code || "",
      full_name: employee.full_name || "",
      date_of_joining: employee.date_of_joining || "",
      mobile_number: employee.mobile_number || "",
      role: employee.role || "SALES_EXECUTIVE",
      district: employee.district || "",
      region_id: employee.region_id || "",
      status: employee.status || "ACTIVE",
      home_address: employee.home_address || "",
      google_location: employee.google_location || "",
      blood_group: employee.blood_group || "",
      aadhar_number: employee.aadhar_number || "",
      pan_number: employee.pan_number || "",
      emergency_contact: employee.emergency_contact || "",
      date_of_birth: employee.date_of_birth || "",
      bank_account_number: employee.bank_account_number || "",
      bank_name: employee.bank_name || "",
      ifsc_code: employee.ifsc_code || "",
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      employee_code: "",
      full_name: "",
      date_of_joining: "",
      mobile_number: "",
      role: "SALES_EXECUTIVE",
      district: "",
      region_id: user?.region_id || "",
      status: "ACTIVE",
      home_address: "",
      google_location: "",
      blood_group: "",
      aadhar_number: "",
      pan_number: "",
      emergency_contact: "",
      date_of_birth: "",
      bank_account_number: "",
      bank_name: "",
      ifsc_code: "",
    });
  };

  const getRoleBadge = (role) => {
    const styles = {
      AREA_MANAGER: "bg-purple-100 text-purple-700 border-purple-200",
      SALES_EXECUTIVE: "bg-blue-100 text-blue-700 border-blue-200",
      PROMOTOR: "bg-amber-100 text-amber-700 border-amber-200",
    };
    const labels = {
      AREA_MANAGER: "Area Manager",
      SALES_EXECUTIVE: "Sales Executive",
      PROMOTOR: "Promotor",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[role] || "bg-slate-100 text-slate-700"}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    return status === "ACTIVE" ? (
      <Badge variant="outline" className="badge-success">Active</Badge>
    ) : (
      <Badge variant="outline" className="badge-default">Inactive</Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Employees</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your sales team</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="employees-region-switcher"
          />
          <SyncNowButton entity="employees" />
          {canEdit && (
            <Button
              onClick={() => {
                setEditingEmployee(null);
                resetForm();
                setIsDialogOpen(true);
              }}
              className="btn-primary"
              data-testid="add-employee-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, code, or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="employee-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="AREA_MANAGER">Area Manager</SelectItem>
              <SelectItem value="SALES_EXECUTIVE">Sales Executive</SelectItem>
              <SelectItem value="PROMOTOR">Promotor</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[150px]" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
            </SelectContent>
          </Select>
          {isSuperAdmin && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="region-filter">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="GJ">Gujarat</SelectItem>
                <SelectItem value="RJ">Rajasthan</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={districtFilter} onValueChange={setDistrictFilter}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="district-filter">
              <SelectValue placeholder="All Districts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              <SelectItem value="Ahmedabad">Ahmedabad</SelectItem>
              <SelectItem value="Gandhinagar">Gandhinagar</SelectItem>
              <SelectItem value="Rajkot">Rajkot</SelectItem>
              <SelectItem value="Surat">Surat</SelectItem>
              <SelectItem value="Vadodara">Vadodara</SelectItem>
              <SelectItem value="Banswara">Banswara</SelectItem>
              <SelectItem value="Udaipur">Udaipur</SelectItem>
              <SelectItem value="Jaipur">Jaipur</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="employees-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Code</th>
                <th>Role</th>
                <th>Region</th>
                <th>Mobile</th>
                <th>Joined</th>
                <th>Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="empty-state">
                      <Users className="empty-state-icon" />
                      <p className="empty-state-title">No employees found</p>
                      <p className="empty-state-description">
                        Add your first employee to get started.
                      </p>
                      <Button
                        onClick={() => setIsDialogOpen(true)}
                        className="btn-primary"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Employee
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                filterByRegion(employees, (e) => e.region_id).map((employee) => (
                  <tr key={employee.employee_id} data-testid={`employee-row-${employee.employee_id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => { setSelectedEmployee(employee); setProfileOpen(true); }}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#8B1A1A]/10 flex items-center justify-center">
                          <span className="text-[#8B1A1A] font-medium text-sm">
                            {employee.full_name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{employee.full_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="font-mono text-sm">{employee.employee_code}</td>
                    <td>{getRoleBadge(employee.role)}</td>
                    <td>
                      {employee.region_id === "GJ" ? (
                        <Badge className="bg-[#C0272D]/10 text-[#C0272D] border-[#C0272D]/20 text-[10px]">Gujarat</Badge>
                      ) : employee.region_id === "RJ" ? (
                        <Badge className="bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20 text-[10px]">Rajasthan</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Phone className="w-3 h-3" />
                        {employee.mobile_number}
                      </div>
                    </td>
                    <td>{employee.date_of_joining}</td>
                    <td>{getStatusBadge(employee.status)}</td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(employee)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!employee.linked_user_id && (
                            <DropdownMenuItem
                              onClick={() => openCreateLogin(employee)}
                              data-testid={`create-login-${employee.employee_id}`}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Create Login
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(employee.employee_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Basic Info</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Employee Code</label>
                <Input value={formData.employee_code} onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })} placeholder="Auto-generated if empty" data-testid="input-employee-code" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Full Name *</label>
                <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Full Name" required data-testid="input-full-name" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Mobile Number</label>
                <Input value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })} placeholder="+91..." data-testid="input-mobile" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date of Joining</label>
                <Input type="date" value={formData.date_of_joining} onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })} data-testid="input-doj" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date of Birth</label>
                <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} data-testid="input-dob" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Role</label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="input-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AREA_MANAGER">Area Manager</SelectItem>
                    <SelectItem value="SALES_EXECUTIVE">Sales Executive</SelectItem>
                    <SelectItem value="PROMOTOR">Promotor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Region</label>
                <Select value={formData.region_id || ""} onValueChange={(value) => setFormData({ ...formData, region_id: value })}>
                  <SelectTrigger data-testid="input-region"><SelectValue placeholder="Select Region" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GJ">Gujarat</SelectItem>
                    <SelectItem value="RJ">Rajasthan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="input-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2 border-t">Personal Details</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Home Address</label>
                <Input value={formData.home_address} onChange={(e) => setFormData({ ...formData, home_address: e.target.value })} placeholder="Home address" data-testid="input-home-address" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Google Location (Lat,Lng)</label>
                <Input value={formData.google_location} onChange={(e) => setFormData({ ...formData, google_location: e.target.value })} placeholder="e.g. 23.0225,72.5714" data-testid="input-google-loc" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Blood Group</label>
                <Select value={formData.blood_group || ""} onValueChange={(value) => setFormData({ ...formData, blood_group: value })}>
                  <SelectTrigger data-testid="input-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Emergency Contact</label>
                <Input value={formData.emergency_contact} onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })} placeholder="+91..." data-testid="input-emergency" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">District</label>
                <Input value={formData.district || ""} onChange={(e) => setFormData({ ...formData, district: e.target.value })} placeholder="e.g. Banaskantha" data-testid="input-district" />
              </div>
            </div>

            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2 border-t">Documents & Banking</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Aadhar Number</label>
                <Input value={formData.aadhar_number} onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })} placeholder="XXXX XXXX XXXX" data-testid="input-aadhar" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">PAN Number</label>
                <Input value={formData.pan_number} onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })} placeholder="ABCDE1234F" data-testid="input-pan" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Bank Name</label>
                <Input value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Bank name" data-testid="input-bank-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Account Number</label>
                <Input value={formData.bank_account_number} onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} placeholder="Account number" data-testid="input-account-num" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">IFSC Code</label>
                <Input value={formData.ifsc_code} onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })} placeholder="IFSC code" data-testid="input-ifsc" />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="submit-employee">
                {editingEmployee ? "Update" : "Add"} Employee
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Profile View */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="employee-profile-dialog">
          {selectedEmployee && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#8B1A1A]/10 flex items-center justify-center">
                    <span className="text-[#8B1A1A] font-bold text-xl">
                      {selectedEmployee.full_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <DialogTitle className="text-lg">{selectedEmployee.full_name}</DialogTitle>
                    <p className="text-sm text-slate-500">{selectedEmployee.employee_code} &middot; {selectedEmployee.role?.replace("_", " ")}</p>
                  </div>
                  <div className="ml-auto flex gap-2">
                    {getStatusBadge(selectedEmployee.status)}
                    <Button size="sm" variant="outline" onClick={() => { setProfileOpen(false); openEditDialog(selectedEmployee); }}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <ProfileField label="Mobile" value={selectedEmployee.mobile_number} />
                  <ProfileField label="Region" value={selectedEmployee.region_id === "GJ" ? "Gujarat" : selectedEmployee.region_id === "RJ" ? "Rajasthan" : "—"} />
                  <ProfileField label="District" value={selectedEmployee.district} />
                  <ProfileField label="Date of Joining" value={selectedEmployee.date_of_joining} />
                  <ProfileField label="Date of Birth" value={selectedEmployee.date_of_birth} />
                  <ProfileField label="Blood Group" value={selectedEmployee.blood_group} />
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Personal</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <ProfileField label="Home Address" value={selectedEmployee.home_address} />
                    <ProfileField label="Google Location" value={selectedEmployee.google_location} />
                    <ProfileField label="Emergency Contact" value={selectedEmployee.emergency_contact} />
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Documents</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <ProfileField label="Aadhar Number" value={selectedEmployee.aadhar_number} sensitive />
                    <ProfileField label="PAN Number" value={selectedEmployee.pan_number} sensitive />
                  </div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Banking</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <ProfileField label="Bank Name" value={selectedEmployee.bank_name} />
                    <ProfileField label="Account No." value={selectedEmployee.bank_account_number} sensitive />
                    <ProfileField label="IFSC Code" value={selectedEmployee.ifsc_code} />
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Login Dialog (Item 3 — link employee to user account) */}
      <Dialog open={!!loginEmployee} onOpenChange={(v) => !v && setLoginEmployee(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="create-login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#8B1A1A]" /> Create Login for {loginEmployee?.full_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLogin} className="space-y-3">
            <p className="text-xs text-slate-500">
              This will create a User Management entry linked to <span className="font-semibold">{loginEmployee?.full_name}</span> ({loginEmployee?.employee_code || loginEmployee?.employee_id}).
              The same email + password will work on web and the mobile app.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">Email (login ID)</label>
              <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Temporary Password</label>
              <Input type="text" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required minLength={6} placeholder="Min 6 characters — share with employee" data-testid="login-password-input" />
              <p className="text-[10px] text-slate-400 mt-1">Tip: Share this password securely. Employee can change it after first login.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Role</label>
              <Select value={loginForm.role} onValueChange={(v) => setLoginForm({ ...loginForm, role: v })}>
                <SelectTrigger data-testid="login-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALES_REP">Sales Rep / Salesman</SelectItem>
                  <SelectItem value="AREA_MANAGER">Area Manager</SelectItem>
                  <SelectItem value="FIELD_EXECUTIVE">Field Executive</SelectItem>
                  <SelectItem value="ACCOUNTS_ADMIN">Accounts Admin</SelectItem>
                  <SelectItem value="SALES_ADMIN">Sales Admin</SelectItem>
                  <SelectItem value="FACTORY_ADMIN">Factory Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 p-2.5 bg-emerald-50 rounded-md border border-emerald-200 cursor-pointer">
              <input type="checkbox" checked={loginForm.mobile_login_enabled} onChange={(e) => setLoginForm({ ...loginForm, mobile_login_enabled: e.target.checked })} className="accent-emerald-600 w-4 h-4" data-testid="login-mobile-toggle" />
              <span className="text-xs text-slate-700"><span className="font-semibold">Allow mobile app login</span> — recommended for field salesmen</span>
            </label>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setLoginEmployee(null)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loginCreating} className="flex-1 bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="submit-create-login-btn">
                {loginCreating ? "Creating..." : "Create Login"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProfileField = ({ label, value, sensitive }) => (
  <div data-testid={`profile-field-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <p className="text-slate-400 text-xs">{label}</p>
    <p className="text-slate-800 font-medium">{value || <span className="text-slate-300">—</span>}</p>
  </div>
);

export default EmployeesPage;
